import merge from 'deepmerge';
import { compact, concat, first, flatMap, groupBy, isEmpty, map, partition, sortBy, take, values } from 'lodash';
import { Locale } from 'locale-enum';
import moment from 'moment';

import { ItemStatusEnum } from '@pimm/services/lib/store-inspections';
import { FormInputSources } from '../consts';
import { BaseReport, StoreFormReport } from './store-form-report';
import { isTempProbeManual } from '../_helper';

namespace ICriticalControlPointsReport {
  export type Item = Omit<BaseReport.Item, 'inputs'> & {
    dayparts: (Omit<BaseReport.Daypart, 'formGroups'> & { inputs: Record<string, BaseReport.Input[]> })[];
  };

  export type Section = Omit<BaseReport.Section, 'data'> & {
    data: Item[];
  };

  export type Form = Omit<BaseReport.Form, 'sections'> & {
    sections: Section[];
  };

  export type FormGroup = Omit<BaseReport.FormGroup, 'forms'> & {
    forms: Form[];
  };

  export type DaypartLeaders = Omit<BaseReport.Daypart, 'formGroups'>[];

  export type ReportDay = Omit<BaseReport.ReportDay, 'dayparts'> & {
    date: string;
    daypartLeaders: DaypartLeaders;
    formGroups: FormGroup[];
  };
}

export class CriticalControlPointsReport extends StoreFormReport {
  public composeReport(locale: Locale): any {
    this.locale = locale;
    const reportDays = this.reportData?.reportDays ?? [];

    //#region Helpers

    /**
     * Merge two arrays of objects by a unique key.
     * If an object with the same key exists in both arrays,
     * it will be deeply merged using `deepmerge`.
     *
     * @param existing - The current array (accumulator).
     * @param incoming - The new array to merge in.
     * @param idKey - The property name that uniquely identifies an object.
     * @returns A new merged array of objects.
     */
    const mergeById = <T>(existing: T[], incoming: T[], idKey: keyof T): T[] => {
      const map = new Map<string, T>();

      // Store all existing entries in a Map for quick lookup by id
      existing.forEach((e: any) => map.set(e[idKey], e));

      incoming.forEach((i: any) => {
        const id = i[idKey];
        if (!id) return;

        if (map.has(id)) {
          // If already exists → deep merge incoming with existing
          map.set(id, merge(map.get(id)!, i, { arrayMerge }));
        } else {
          // If new → just insert
          map.set(id, i);
        }
      });

      return Array.from(map.values());
    };

    /**
     * Custom array merge strategy for deepmerge.
     * - If the array contains identifiable entities (by group/form/section/item id),
     *   merge them by ID instead of concatenating.
     * - If the array contains raw inputs, concatenate them.
     * - Otherwise, replace destination with source.
     */
    const arrayMerge = (dest: any[], source: any[], options: any): any[] => {
      if (dest.length && typeof dest[0] === 'object') {
        if ('packageDetailsGroupId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsGroupId');
        }
        if ('packageDetailsFormId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsFormId');
        }
        if ('packageDetailsSectionId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsSectionId');
        }
        if ('sectionLineItemId' in dest[0]) {
          return mergeById(dest, source, 'sectionLineItemId');
        }
        if ('inputs' in dest[0]) {
          // For input arrays, just concatenate (no merging by id)
          return dest.concat(source);
        }
      }
      // Default: replace with source
      return source;
    };

    /**
     * Flatten a form’s sections into a flat list of items
     * and attach daypart metadata to each item.
     *
     * @returns A ICriticalControlPointsReport.Form with items and dayparts attached.
     */
    const mapItemsByForm = (_daypart: Partial<BaseReport.Daypart>, _form: BaseReport.Form) => {
      const sections = map(_form.sections, ({ data = [], ..._section }) => {
        const section: ICriticalControlPointsReport.Section = {
          ..._section,
          data: data.map(({ inputs: _itemInputs, ..._item }) => {
            // Normalize inputs: pick the last recorded input for each key
            const inputs = Object.entries(_itemInputs).reduce((inputs: Record<string, BaseReport.Input[]>, [inputId, _inputs]) => {
              return { ...inputs, [inputId]: take(_inputs, 1) }; // get the last recorded input
            }, {});
            const item: ICriticalControlPointsReport.Item = {
              dayparts: [{ ..._daypart, inputs }],
              ..._item,
            };
            return item;
          }),
        };
        return section;
      });

      const form: ICriticalControlPointsReport.Form = {
        ..._form,
        sections: sections,
      };
      return form;
    };

    //#endregion

    /**
     * STEP 1
     * Transform all reportDays by:
     *  - Flattening sections → items
     *  - Merging formGroups/forms/items across dayparts by ID
     *  - Attaching daypart metadata to each item
     *
     * @param reportDays - The raw BaseReport.ReportDay[]
     * @returns Flattened and merged ICriticalControlPointsReport.ReportDay[]
     */
    // prettier-ignore
    const normalizedReportByDates = reportDays.reduce((_reportDays: ICriticalControlPointsReport.ReportDay[], { dayparts, ..._reportDay }) => {
      const reportDay: ICriticalControlPointsReport.ReportDay = {
        ..._reportDay,
        daypartLeaders: [],
        formGroups: [],
      };

      for (const { formGroups = [], ..._daypart } of dayparts) {
        if (!formGroups.length) continue;

        // Merge all dayparts for this reportDay
        const nextFormGroups = formGroups.map(({ forms = [], ..._formGroup }) => {
          return { forms: forms.map(_form => mapItemsByForm(_daypart, _form)), ..._formGroup };
        });

        // Merge with accumulated formGroups using custom arrayMerge
        reportDay.formGroups = merge(reportDay.formGroups, nextFormGroups, { arrayMerge });
        // Append daypart leader
        reportDay.daypartLeaders.push(_daypart);
      }

      return [..._reportDays, reportDay];
    }, []);

    /**
     * Step 2
     * Transform combined data into composer readable body
     */

    const reportDay = normalizedReportByDates[0];

    // TODO: create composer type
    const compiledData: Record<string, any> = {
      reports: [],
    };

    if (reportDay) {
      const reportBody: Record<string, any> = {
        ...this.composerBody(),
        formGroups: [],
        reviews: {},
        translations: {
          item: this.translate('item'),
          standard: this.translate('standard'),
          time: this.translate('time'),
          result: this.translate('result'),
          correctiveActions: this.translate('corrective_actions'),
          correctiveActionKeys: this.translate('corrective_action_keys'),
          completedBy: this.translate('completed_by'),
        },
      };

      // Page Leaders
      reportBody.reviews = {
        dayparts: [],
        districtManager: this.composeReviewer({
          ...reportDay.dmReview,
          label: this.translate('district_manager'),
        }),
      };

      // Page Body — Pass 1: collect per-day EOD reviews
      for (const _reportDay of normalizedReportByDates) {
        const hasForms = _reportDay.formGroups.some(({ forms = [] }) => forms.length > 0);

        if (hasForms) {
          const reviewInfo = this.composeReviewer({
            ..._reportDay.eodReview,
            label: `${moment(_reportDay.date).format('ddd').toUpperCase()} - ${this.translate('ops_leader')}`,
          });

          reportBody.reviews.dayparts.push(reviewInfo);
        }
      }

      // Page Body — Pass 2: consolidate form groups across all report days, sorted globally by seq
      const consolidatedFormGroups = sortBy(
        normalizedReportByDates.reduce((acc, _reportDay) => mergeById(acc, _reportDay.formGroups, 'packageDetailsGroupId'), []),
        'seq',
      );

      for (const { forms } of consolidatedFormGroups) {
        if (!forms?.length) continue;

        for (const { sections, ...form } of forms) {
            // Flatten all items from all sections in this form
            const allItems = sections.flatMap(_section => _section.data.filter(_ => _.status !== ItemStatusEnum.Excluded));

            if (!allItems.length) continue;

            // Partition items by tagId (like Opsbee)
            const [untaggedItems, itemsWithTags] = partition(allItems, _ => !_.tag?.id);

            // Calculate input sources for this form
            const inputTypes = forms.flatMap(({ sections }) => {
              return sections.flatMap(({ data }) => compact(data.flatMap(_ => _.inputType?.toLowerCase())));
            });
            const inputSources = FormInputSources.filter(_ => inputTypes.some(_inputType => _inputType.includes(_.keyword)));

            // Append form group header
            reportBody.formGroups.push({
              useDefId: 'formGroupHeader',
              title: this.translate(form.title, form.titleTranslations),
              inputSourceLabel: !isEmpty(inputSources) ? this.translate('temperature_input_sources') : undefined,
              inputSources: inputSources.map(_source => ({ ..._source, label: this.translate(_source.label) })),
            });

            const taggedItems = sortBy(values(groupBy(itemsWithTags, item => item.tag?.id ?? '_')), items => items[0]?.tag?.name);
            // Process sections: untagged items first, then tagged items
            for (const _items of [untaggedItems, ...taggedItems]) {
              if (!_items.length) continue;

              const tag = _items[0]?.tag;

              // Append Section header
              reportBody.formGroups.push({
                useDefId: 'ccpDaypartHeader',
                title: tag ? this.translate(tag.name, tag.translations) : this.translate('Item'),
              });

              for (const _item of _items) {
                const isMulti = _item.dayparts.length > 1;

                for (const _daypart of _item.dayparts) {
                  const item = this.composeItem(_item);

                  const isSkipped = _item.status === ItemStatusEnum.Skipped;
                  const isManualProbe = isTempProbeManual(_item.inputType);

                  const suffix = this.translate(_daypart.label, _daypart.translations);

                  const allInputs = values(_daypart.inputs)
                    .map(_inputs => first(_inputs))
                    .filter(_input => !!_input) as BaseReport.Input[];

                  // Use composeInput which already handles multi-inputs correctly
                  const input = this.composeInput(allInputs, _item.inputType);

                  const inputIcon = FormInputSources.find(_ => allInputs[0]?.inputSource?.toLowerCase().includes(_.keyword))?.icon;

                  reportBody.formGroups.push({
                    ...item,
                    ...input,
                    useDefId: 'ccpDaypartItem',
                    description: isMulti ? `${item.description} (${suffix})` : item.description,
                    inputSource: !isSkipped && isManualProbe && !!inputIcon ? inputIcon : undefined,
                  });
                }
              }
            }
          }
        }

      // Foot Notes
      let formNotes: { form: BaseReport.Form; daypart: BaseReport.Daypart }[] = [];
      let reportNotes: any[] = [];

      // Use original reportDays which should have normalized notes from constructor
      // The constructor normalizes reportNotes from both reportDay.reportNotes and dayparts[].reportNotes into reportDay.notes
      for (const _reportDay of reportDays) {
        const title = `${moment(_reportDay?.date).format('MM/DD/YYYY - ddd').toUpperCase()}`;

        const daypartReportNotes = flatMap(_reportDay.dayparts, (daypart: any) => (daypart as any).reportNotes ?? []);

        // The normalization collects from both reportDay.reportNotes and dayparts[].reportNotes
        // Combine normalized + daypart notes and keep only those flagged includeInReport (default true if missing)
        const allNotes = concat(_reportDay.notes ?? [], daypartReportNotes).filter(_note => _note?.includeInReport);

        const reportDayWithNotes = {
          ..._reportDay,
          notes: allNotes,
        };

        const composedNotes = this.composeReportNotes(reportDayWithNotes, title);
        // Do not filter composedNotes (they are defs without include flags); keep all composed entries
        reportNotes = concat(reportNotes, composedNotes);

        for (const { formGroups, ..._daypart } of _reportDay.dayparts) {
          const forms = compact(flatMap(formGroups, _ => _.forms)).filter(_ => !!_.notes?.filter(_ => _.includeInReport)?.length);
          forms.forEach(_form => formNotes.push({ form: _form, daypart: _daypart }));
        }
      }

      // append report notes
      if (reportNotes.length) {
        reportBody.notes.push({ useDefId: 'noteTitle', title: this.translate('report_notes') });
        reportNotes.forEach(_def => reportBody.notes.push(_def));
      }

      // append form notes
      if (formNotes.length) {
        const forms = sortBy(formNotes, _ => this.translate(_.form.title, _.form.titleTranslations));
        const notes = forms.flatMap(_ => this.composeFormNotes(_.form, _.daypart));

        reportBody.notes.push({ useDefId: 'noteTitle', title: this.translate('form_notes') });
        notes.forEach(_def => reportBody.notes.push(_def));
      }

      compiledData.reports.push(reportBody);
    }

    return compiledData.reports[0];
  }
}
